// [PURPOSE]: Character Context - Centralized character state management
// [WHY]: Extract character-related state from monolithic App.jsx (16,437 lines)
// [INTEGRATION]: Provides character stats, progression, spells, and activity via React Context
// [CREATED]: October 24, 2025 - Phase 4A: Architectural Refactoring

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSettings } from './SettingsContext';
import { systemBus, EVENTS } from '../services/systemBus';
import { processBastionTurn } from '../data/bastionData';
import { concentrationSystem } from '../services/concentrationSystem'; // [PHASE 0.2 - NOV 11, 2025]: Concentration tracking
import effectRegistry from '../services/effectRegistry'; // [PHASE 1.2 FIX - NOV 15, 2025]: Re-register effects on load
import { getFeatData } from '../data/srdFeats'; // [PHASE 1.2 FIX - NOV 15, 2025]: For feat effect re-registration
// [Fix 1.10 — APR 2026 release triage]: Custom resource pools live in a global
//   in-memory registry; on character load we walk customFeats and re-register
//   each pool so the autocomplete + ClassResourcesPanel see them after reload.
import { registerCustomResourcePool } from '../data/classResources';
import { effectCalculator } from '../services/effectCalculator'; // [FIX NOV 18, 2025]: For subclass spell auto-sync
import { SRD_SPELLS } from '../data/srdSpells'; // [FIX NOV 18, 2025]: For subclass spell data lookup
import { safeLocalStorageSet } from '../utils/storageValidator';
// [D3 / #103]: Runtime gate that throws (dev/test) or logs (prod) if v1
//   code attempts to write to a path reserved for a v1.x deferred system.
//   See src/services/reservedFields.js for the full registry + rationale.
import { assertNoReservedWrites } from '../services/reservedFields';
// [Phase B / #233]: Condition numericEffects re-registration fix.
//   playerStats.conditions is an array of STRING keys (e.g. ['grappled']).
//   We look up each key here to get the structured numericEffects for the registry.
import { SRD_CONDITIONS } from '../data/srdConditions';

/**
 * CharacterContext provides all character-related state and functions
 *
 * This context manages:
 * - Player stats (stats, level, classes, HP, death saves)
 * - Currencies (CP, SP, EP, GP, PP)
 * - Spell system (known, prepared, slots, custom spells)
 * - Activity log and journal entries
 * - Time system and calendar
 * - Bastion system (D&D 2024)
 * - Quick bonuses and temporary stats
 * - Character wizards and UI state
 */
const CharacterContext = createContext(null);

export const CharacterProvider = ({ children }) => {
  const { settings } = useSettings();

  // ============================================================================
  // CORE CHARACTER STATE
  // ============================================================================

  const [playerStats, setPlayerStatsRaw] = useState({
    name: '',
    class: '',  // Primary class for backwards compatibility
    level: 1,   // Total character level
    classes: [], // Array of { className: string, level: number, spellbook: [] }
    strength: 10,
    // [PHASE 4.4]: carryCapacity removed - now derived in App.jsx via effectCalculator
    useWeightLimit: true,
    useBagLimit: true,
    bagSize: [10, 10],
    hasReceivedStarterPack: false,
    // Rest System Properties
    hitDice: { d6: 0, d8: 0, d10: 0, d12: 0 },
    hitDiceUsed: { d6: 0, d8: 0, d10: 0, d12: 0 },
    maxHitPoints: 8,
    currentHitPoints: 8,
    temporaryHitPoints: 0,
    deathSaves: { successes: 0, failures: 0 },
    startingGoldRolled: false,
    startingGoldResult: null,
    inspiration: 0,  // D&D 5e Inspiration points
    birthday: null,  // { day: number, month: number, birthYear: number } or null if not set
    bastion: null  // D&D 2024 Bastion System (unlocks at level 5)
  });

  // [D3 / #103]: Gated setPlayerStats. Wraps the raw useState setter and
  //   intercepts both value-and-functional-updater shapes. On every commit
  //   it asks src/services/reservedFields.js whether the new state writes
  //   to any RESERVED_FIELDS path. In dev/test the gate throws; in prod it
  //   logs and lets the write through (graceful degrade — same posture as
  //   systemBus.emit's falsy-event guard).
  //
  //   Reference equality between prev and next at a reserved path is
  //   treated as "spread-through, not a write" — so v1.x reserved data
  //   loaded via importInventory or loadCharacter round-trips without
  //   tripping the gate, as long as the load path doesn't construct fresh
  //   sub-objects under the reserved keys.
  const setPlayerStats = useCallback((updater) => {
    if (typeof updater === 'function') {
      setPlayerStatsRaw((prev) => {
        const next = updater(prev);
        assertNoReservedWrites(next, prev, 'CharacterContext.setPlayerStats');
        return next;
      });
    } else {
      setPlayerStatsRaw((prev) => {
        assertNoReservedWrites(updater, prev, 'CharacterContext.setPlayerStats');
        return updater;
      });
    }
  }, []);

  const [currencies, setCurrencies] = useState({
    cp: 0, sp: 0, ep: 0, gp: 0, pp: 0
  });

  const [skillProficiencies, setSkillProficiencies] = useState({});

  const [temporaryStats, setTemporaryStats] = useState({}); // Track temporary stat modifications {stat: {add: number, set: number}}

  // ============================================================================
  // TIME AND CALENDAR STATE
  // ============================================================================

  const [currentGameDay, setCurrentGameDay] = useState(1);

  // [TIER 1.3 FIX APR 2026 — Calendar/Rest desync]: Stable game-day-1 epoch.
  //   The in-game clock STARTS at game-day-1 08:00 and advances ONLY through
  //   advanceDay/passTime/long-rest etc. RestPanel + CalendarPanel must read
  //   timeSystem.currentTime (game time), NOT new Date() (wall clock).
  //   Without this seed every consumer fell back to wall clock, the first
  //   long rest stamped lastLongRest = wallClock + 8h, and canLongRest's
  //   "24h elapsed" check went negative-forever — the long-rest button was
  //   permanently disabled. See docs/superpowers/plans/2026-04-28-release-
  //   triage-matrix.md (Tier 1.3) for the full root-cause writeup.
  const [timeSystem, setTimeSystem] = useState(() => ({
    currentHour: 8,  // 8 AM start
    currentTime: new Date(2024, 0, 1, 8, 0, 0), // game day 1, 08:00 — single source for canRest checks
    lastShortRest: null,
    lastLongRest: null,
    exhaustionLevel: 0,
    timeSinceLastRest: 0
  }));

  const [calendarSettings, setCalendarSettings] = useState({
    progressDayOnLongRest: true,
    showSeasonalEffects: true,
    startYear: 1492, // DR (Dale Reckoning)
    showLunarPhases: true,
    trackHolidays: true
  });

  // ============================================================================
  // SPELL SYSTEM STATE
  // ============================================================================

  const [knownSpells, setKnownSpells] = useState([]);
  const [preparedSpells, setPreparedSpells] = useState([]);
  const [customSpells, setCustomSpells] = useState([]);
  const [manualSpellSlots, setManualSpellSlots] = useState(false);
  const [spellSlots, setSpellSlots] = useState({
    1: { max: 0, used: 0 },
    2: { max: 0, used: 0 },
    3: { max: 0, used: 0 },
    4: { max: 0, used: 0 },
    5: { max: 0, used: 0 },
    6: { max: 0, used: 0 },
    7: { max: 0, used: 0 },
    8: { max: 0, used: 0 },
    9: { max: 0, used: 0 }
  });
  const [activeSpellbookClass, setActiveSpellbookClass] = useState('all'); // For multiclass spellbook tabs
  const [collapsedSpellLevels, setCollapsedSpellLevels] = useState(() => {
    try {
      const saved = localStorage.getItem('collapsedSpellLevels');
      return saved ? JSON.parse(saved) : { 4: true, 5: true, 6: true, 7: true, 8: true, 9: true };
    } catch {
      return { 4: true, 5: true, 6: true, 7: true, 8: true, 9: true };
    }
  });

  // Custom spell components database
  const [customComponents, setCustomComponents] = useState(() => {
    const saved = localStorage.getItem('customComponents');
    return saved ? JSON.parse(saved) : [];
  });

  // [PHASE 4D SESSION 2]: Concentration Tracking System
  // [PURPOSE]: Track current concentration spell to enforce D&D 5e concentration rules
  // [WHY]: Prevents stacking multiple concentration spells (PHB p.203)
  const [currentConcentration, setCurrentConcentration] = useState(null);

  // [DEPRECATED - PHASE 0.2.5 NOV 11, 2025]: Pending Concentration Save
  // [WHY DEPRECATED]: VitalStatsCard now automatically rolls and resolves concentration saves
  // [KEPT FOR]: Backward compatibility (exported as null)
  // [OLD PURPOSE]: Track when character needs to make concentration save due to damage
  const [pendingConcentrationSave] = useState(null); // Always null now
  // OLD Format: { spellName: string, spellLevel: number, dc: number, damageTaken: number }

  // ============================================================================
  // ACTIVITY LOG AND JOURNAL STATE
  // ============================================================================

  const [activityLog, setActivityLog] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [rollTables, setRollTables] = useState([]); // [PHASE 4C - NOV 1, 2025]: Roll tables for encounters, loot, events, etc.
  const [craftingProjects, setCraftingProjects] = useState([]);
  const [dmEvents, setDmEvents] = useState([]);

  // ============================================================================
  // QUICK BONUSES AND INSPIRATION
  // ============================================================================

  const [activeQuickBonuses, setActiveQuickBonuses] = useState([]);
  const [customQuickBonuses, setCustomQuickBonuses] = useState([]);
  const [inspirationPrompt, setInspirationPrompt] = useState(null);

  // ============================================================================
  // UI STATE (Character-Related Wizards and Modals)
  // ============================================================================

  const [showCharacterWizard, setShowCharacterWizard] = useState(false);
  const [showLevelUpWizard, setShowLevelUpWizard] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [levelUpLevel, setLevelUpLevel] = useState(null);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // Activity logging helper
  const logActivity = useCallback((message, type = 'general', details = {}) => {
    const entry = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      message,
      type, // combat, items, currency, spells, character, general
      details
    };
    setActivityLog(prev => [entry, ...prev].slice(0, 500)); // Keep last 500 entries
  }, []);

  // [R1.D2.2]: Single entry point for advancing currentGameDay. Emits
  // DAY_ADVANCED so downstream subscribers (rest recovery, dawn recharge,
  // downtime tick, bastion turn) react in their own domain. `source` is
  // required so callers self-identify in logs and so subscribers can
  // branch behavior (e.g., long-rest skips the rest-recovery subscriber
  // because RestPanel handled recovery itself).
  // Spec: docs/superpowers/specs/2026-04-28-r1-release-readiness-design.md §D2.
  //
  // [TIER 1.3 FIX APR 2026 — Calendar/Rest desync]: advanceDay now also
  //   pushes timeSystem.currentTime forward by `days * 24h` by default.
  //   Without this, the in-game clock and the calendar drifted apart on
  //   every calendar jump or long-rest day-bump, and canLongRest's
  //   elapsed-time check compared a frozen timeSystem.currentTime to
  //   lastLongRest values that were already in the future. The new
  //   currentTime rides along on the DAY_ADVANCED payload so downstream
  //   subscribers all see one consistent timestamp.
  //
  //   Callers that have already moved the clock themselves (e.g.,
  //   long-rest's computeLongRestAdvance crossing midnight, calendar
  //   jumps that compute their own newTime via computeJumpRestSync) can
  //   pass `advanceClock: false` to bump the day counter only. Default
  //   `true` keeps simple sources (downtime, bastion-turn) honest without
  //   ceremony.
  const advanceDay = useCallback(({ days = 1, source, advanceClock = true } = {}) => {
    if (!source) {
      throw new Error('advanceDay requires a `source` field (e.g., "long-rest", "calendar-manual", "downtime", "bastion-turn")');
    }
    // Compute newCurrentTime from the closure value of timeSystem. We
    // intentionally do NOT compute this inside a functional updater —
    // updater scheduling means the cross-setter closure variable
    // (newCurrentTime) is not guaranteed to be populated by the time the
    // setCurrentGameDay updater runs and emits DAY_ADVANCED. The closure
    // value is fresh enough for advanceDay's contract (one user action
    // per call), and downstream subscribers reading the event payload
    // get a correct Date instead of null.
    const baseCurrentTime = timeSystem?.currentTime instanceof Date
      ? timeSystem.currentTime
      : new Date(timeSystem?.currentTime || Date.now());
    const newCurrentTime = advanceClock
      ? new Date(baseCurrentTime.getTime() + days * 24 * 60 * 60 * 1000)
      : baseCurrentTime;

    if (advanceClock) {
      setTimeSystem(prev => ({ ...prev, currentTime: newCurrentTime }));
    }
    setCurrentGameDay(prev => {
      const next = prev + days;
      systemBus.emit(EVENTS.DAY_ADVANCED, {
        from: prev,
        to: next,
        source,
        days,
        // [TIER 1.3]: New currentTime on the payload — subscribers should
        //   prefer this over reading timeSystem from closure (which can
        //   be stale across batched setStates).
        currentTime: newCurrentTime,
      });
      return next;
    });
  }, [timeSystem]);

  // ============================================================================
  // CHARACTER ACTIONS
  // ============================================================================

  const updateHP = useCallback((newHP) => {
    setPlayerStats(prev => {
      const oldHP = prev.currentHitPoints;
      const updatedStats = {
        ...prev,
        currentHitPoints: Math.max(0, Math.min(newHP, prev.maxHitPoints))
      };

      // [INTEGRATION]: Emit HP_CHANGED event
      systemBus.emit(EVENTS.HP_CHANGED, {
        oldHP,
        newHP: updatedStats.currentHitPoints,
        maxHP: prev.maxHitPoints
      });

      return updatedStats;
    });
  }, []);

  const updateMaxHP = useCallback((newMaxHP) => {
    setPlayerStats(prev => ({
      ...prev,
      maxHitPoints: Math.max(1, newMaxHP)
    }));
  }, []);

  const updateTemporaryHP = useCallback((newTempHP) => {
    setPlayerStats(prev => ({
      ...prev,
      temporaryHitPoints: Math.max(0, newTempHP)
    }));
    // [FIX APR 2026 — F-40]: Emit event so other systems can react to temp HP changes
    systemBus.emit(EVENTS.TEMP_HP_CHANGED, { newTempHP: Math.max(0, newTempHP) });
  }, []);

  const updateDeathSaves = useCallback((successes, failures) => {
    setPlayerStats(prev => ({
      ...prev,
      deathSaves: {
        successes: Math.max(0, Math.min(3, successes)),
        failures: Math.max(0, Math.min(3, failures))
      }
    }));

    // [INTEGRATION]: Emit DEATH_SAVE event
    systemBus.emit(EVENTS.DEATH_SAVE_ROLLED, {
      successes,
      failures,
      isStabilized: successes >= 3,
      isDead: failures >= 3
    });
  }, []);

  const resetDeathSaves = useCallback(() => {
    setPlayerStats(prev => ({
      ...prev,
      deathSaves: { successes: 0, failures: 0 }
    }));
  }, []);

  const updateInspiration = useCallback((amount) => {
    setPlayerStats(prev => {
      const oldInspiration = prev.inspiration || 0;
      const newInspiration = Math.max(0, Math.min(5, amount));

      // [INTEGRATION]: Emit INSPIRATION_CHANGED event
      systemBus.emit(EVENTS.INSPIRATION_CHANGED, {
        oldAmount: oldInspiration,
        newAmount: newInspiration,
        consumed: newInspiration < oldInspiration
      });

      return {
        ...prev,
        inspiration: newInspiration
      };
    });
  }, []);

  const updateLevel = useCallback((newLevel) => {
    setPlayerStats(prev => {
      const oldLevel = prev.level;

      // [INTEGRATION]: Emit LEVEL_UP event
      if (newLevel > oldLevel) {
        systemBus.emit(EVENTS.LEVEL_UP, {
          oldLevel,
          newLevel,
          characterName: prev.name
        });
      }

      return {
        ...prev,
        level: Math.max(1, Math.min(20, newLevel))
      };
    });
  }, []);

  const updateXP = useCallback((newXP) => {
    setPlayerStats(prev => {
      const oldXP = prev.experience || 0;

      // [INTEGRATION]: Emit XP_CHANGED event
      systemBus.emit(EVENTS.XP_CHANGED, {
        oldXP,
        newXP,
        gained: newXP > oldXP
      });

      return {
        ...prev,
        experience: Math.max(0, newXP)
      };
    });
  }, []);

  // ============================================================================
  // CURRENCY ACTIONS
  // ============================================================================

  const updateCurrency = useCallback((denomination, amount) => {
    setCurrencies(prev => {
      const oldAmount = prev[denomination] || 0;
      const newAmount = Math.max(0, amount);
      const change = newAmount - oldAmount;

      // [INTEGRATION]: Emit CURRENCY_CHANGED event
      systemBus.emit(EVENTS.CURRENCY_CHANGED, {
        denomination,
        oldAmount,
        newAmount,
        change
      });

      // [v1.1]: Log currency changes to activity
      if (change !== 0) {
        logActivity(`${change > 0 ? 'Gained' : 'Spent'} ${Math.abs(change)} ${denomination.toUpperCase()}`, 'currency', {
          denomination, oldAmount, newAmount, change
        });
      }

      return {
        ...prev,
        [denomination]: newAmount
      };
    });
  }, [logActivity]);

  const addCurrency = useCallback((denomination, amount) => {
    setCurrencies(prev => ({
      ...prev,
      [denomination]: Math.max(0, (prev[denomination] || 0) + amount)
    }));
    // [FIX APR 2026 — F-39]: Emit event + log (matching updateCurrency behavior)
    systemBus.emit(EVENTS.CURRENCY_CHANGED, { denomination, amount, action: 'add' });
    logActivity(`Added ${amount} ${denomination}`, 'inventory', { denomination, amount });
  }, [logActivity]);

  const subtractCurrency = useCallback((denomination, amount) => {
    setCurrencies(prev => ({
      ...prev,
      [denomination]: Math.max(0, (prev[denomination] || 0) - amount)
    }));
    // [FIX APR 2026 — F-39]: Emit event + log (matching updateCurrency behavior)
    systemBus.emit(EVENTS.CURRENCY_CHANGED, { denomination, amount, action: 'subtract' });
    logActivity(`Spent ${amount} ${denomination}`, 'inventory', { denomination, amount });
  }, [logActivity]);

  // ============================================================================
  // SPELL ACTIONS
  // ============================================================================

  const useSpellSlot = useCallback((level) => {
    setSpellSlots(prev => {
      const currentSlot = prev[level];
      if (!currentSlot || currentSlot.used >= currentSlot.max) {
        return prev; // No slots available
      }

      const updated = {
        ...prev,
        [level]: {
          ...currentSlot,
          used: currentSlot.used + 1
        }
      };

      // [INTEGRATION]: Emit SPELL_SLOT_USED event
      systemBus.emit(EVENTS.SPELL_SLOT_USED, {
        level,
        remaining: currentSlot.max - (currentSlot.used + 1)
      });

      return updated;
    });
  }, []);

  const restoreSpellSlot = useCallback((level, amount = 1) => {
    setSpellSlots(prev => {
      const currentSlot = prev[level];
      if (!currentSlot) return prev;

      return {
        ...prev,
        [level]: {
          ...currentSlot,
          used: Math.max(0, currentSlot.used - amount)
        }
      };
    });
  }, []);

  const restoreAllSpellSlots = useCallback(() => {
    setSpellSlots(prev => {
      // [FIX - NOV 3]: Add null check to prevent "Cannot convert undefined or null to object"
      if (!prev || typeof prev !== 'object') {
        console.warn('⚠️ restoreAllSpellSlots called with invalid prev:', prev);
        return {};
      }
      const restored = {};
      Object.keys(prev).forEach(level => {
        restored[level] = {
          ...prev[level],
          used: 0
        };
      });
      return restored;
    });
  }, []);

  const addKnownSpell = useCallback((spell) => {
    setKnownSpells(prev => {
      if (prev.some(s => s.name === spell.name)) {
        return prev; // Already known
      }
      // [v1.1]: Log spell learning
      logActivity(`Learned spell: ${spell.name}`, 'spells', { level: spell.level });
      return [...prev, spell];
    });
  }, [logActivity]);

  const removeKnownSpell = useCallback((spellName) => {
    logActivity(`Forgot spell: ${spellName}`, 'spells');
    setKnownSpells(prev => prev.filter(s => s.name !== spellName));
  }, [logActivity]);

  const prepareSpell = useCallback((spell) => {
    setPreparedSpells(prev => {
      if (prev.some(s => s.name === spell.name)) {
        return prev; // Already prepared
      }

      // [INTEGRATION]: Emit SPELL_PREPARED event
      systemBus.emit(EVENTS.SPELL_PREPARED, {
        spell: spell.name,
        level: spell.level
      });

      // [v1.1]: Log spell preparation
      logActivity(`Prepared spell: ${spell.name}`, 'spells', { level: spell.level });

      return [...prev, spell];
    });
  }, [logActivity]);

  const unprepareSpell = useCallback((spellName) => {
    logActivity(`Unprepared spell: ${spellName}`, 'spells');
    setPreparedSpells(prev => {
      // [INTEGRATION]: Emit SPELL_UNPREPARED event
      systemBus.emit(EVENTS.SPELL_UNPREPARED, {
        spell: spellName
      });

      return prev.filter(s => s.name !== spellName);
    });
  }, [logActivity]);

  // ============================================================================
  // CONCENTRATION TRACKING (PHASE 4D SESSION 2)
  // ============================================================================

  // [PURPOSE]: Start concentration on a new spell
  // [WHY]: D&D 5e PHB p.203 - Only one concentration spell at a time
  // [PHASE 0.2.5 - NOV 11, 2025]: Delegates to concentrationSystem service for mechanics
  const startConcentration = useCallback((spellName, spellLevel, source = 'spell') => {
    // [STEP 1]: Call concentrationSystem service for actual mechanics
    // [WHY]: Single source of truth for D&D 5e concentration rules
    const result = concentrationSystem.startConcentration(
      { name: spellName, level: spellLevel, concentration: true },
      playerStats,
      null, // showStatus - not available in CharacterContext
      logActivity
    );

    // [STEP 2]: Update React state for UI (if successful)
    // [WHY]: UI components like VitalStatsCard display currentConcentration
    if (result.success) {
      const concentrationData = {
        spellName,
        spellLevel,
        startTime: new Date(),
        source
      };

      setCurrentConcentration(concentrationData);

      return concentrationData;
    }

    // [STEP 3]: If failed, return null
    return null;
  }, [playerStats, logActivity]);

  // [PURPOSE]: Break concentration on current spell
  // [WHY]: Can be broken by damage, incapacitation, or voluntary choice
  // [PHASE 0.2.5 - NOV 11, 2025]: Delegates to concentrationSystem service for mechanics
  const breakConcentration = useCallback((reason = 'voluntary') => {
    // [STEP 1]: Call concentrationSystem service for actual mechanics
    // [WHY]: Single source of truth for D&D 5e concentration rules
    concentrationSystem.breakConcentration(
      reason,
      null, // showStatus - not available in CharacterContext
      logActivity
    );

    // [STEP 2]: Clear React state for UI
    // [WHY]: UI components like VitalStatsCard display currentConcentration
    setCurrentConcentration(null);
  }, [logActivity]);

  // [PURPOSE]: Check if currently concentrating on a specific spell
  // [PHASE 0.2.5 - NOV 11, 2025]: Uses concentrationSystem service
  const isConcentratingOn = useCallback((spellName) => {
    const concentration = concentrationSystem.getActiveConcentration();
    return concentration?.spellName === spellName;
  }, []);

  // [PURPOSE]: Get current concentration info
  // [PHASE 0.2.5 - NOV 11, 2025]: Returns from concentrationSystem service
  const getConcentration = useCallback(() => {
    return concentrationSystem.getActiveConcentration();
  }, []);

  // [DEPRECATED - PHASE 0.2.5 NOV 11, 2025]: Require Concentration Save
  // [WHY DEPRECATED]: VitalStatsCard now automatically calls concentrationSystem.checkConcentration()
  // [KEPT FOR]: Backward compatibility - does nothing now
  // [OLD PURPOSE]: Trigger concentration save when taking damage
  const requireConcentrationSave = useCallback((damageTaken) => {
    // NO-OP: VitalStatsCard handles this automatically now
    console.warn('[CharacterContext] requireConcentrationSave is deprecated. VitalStatsCard handles concentration checks automatically.');
  }, []);

  // [DEPRECATED - PHASE 0.2.5 NOV 11, 2025]: Resolve Concentration Save
  // [WHY DEPRECATED]: VitalStatsCard now automatically rolls and resolves concentration saves
  // [KEPT FOR]: Backward compatibility - does nothing now
  // [OLD PURPOSE]: Handle result of concentration save roll
  const resolveConcentrationSave = useCallback((success) => {
    // NO-OP: VitalStatsCard handles this automatically now
    console.warn('[CharacterContext] resolveConcentrationSave is deprecated. VitalStatsCard handles concentration checks automatically.');
  }, []);

  // ============================================================================
  // QUICK BONUS ACTIONS
  // ============================================================================

  const addQuickBonus = useCallback((bonus) => {
    setActiveQuickBonuses(prev => {
      // Check if bonus already exists
      if (prev.some(b => b.name === bonus.name)) {
        return prev;
      }
      return [...prev, bonus];
    });
    // [FIX APR 2026 — F-41]: Emit event so stat recalculation is triggered
    systemBus.emit(EVENTS.QUICK_BONUS_ADDED, { bonus });
  }, []);

  const removeQuickBonus = useCallback((bonusName) => {
    setActiveQuickBonuses(prev => prev.filter(b => b.name !== bonusName));
    // [FIX APR 2026 — F-41]: Emit event so stat recalculation is triggered
    systemBus.emit(EVENTS.QUICK_BONUS_REMOVED, { bonusName });
  }, []);

  const clearAllQuickBonuses = useCallback(() => {
    setActiveQuickBonuses([]);
  }, []);

  // ============================================================================
  // BASTION SYSTEM (D&D 2024)
  // ============================================================================

  // [PURPOSE]: Auto-unlock bastion at configured level (default 5 per D&D 2024 DMG)
  // [v1.1 FIX]: Was hardcoded to level 1 — now uses settings.bastionUnlockLevel
  useEffect(() => {
    const unlockLevel = settings.bastionUnlockLevel || 5;
    const shouldUnlock = playerStats.level >= unlockLevel && !playerStats.bastion;

    if (shouldUnlock) {
      const isEarlyUnlock = playerStats.level < 5;

      // Initialize bastion with starting facilities
      // [FIX NOV 28, 2025]: Added bastion.id to fix undefined bastionId in turn events
      // [FIX MAY 2026]: `established: false` — auto-unlock creates the bastion
      //   stub at the level threshold but does NOT activate weekly-turn
      //   processing. The user must explicitly click "Begin Bastion Operations"
      //   in BastionPanel to flip `established: true`. Pre-fix bastion turns
      //   fired automatically for any L5+ character — even those who never
      //   wanted a bastion. processBastionTurn (data/bastionData.js) and the
      //   CharacterContext DAY_ADVANCED subscriber both now require
      //   `bastion.established === true` before processing.
      setPlayerStats(prev => ({
        ...prev,
        bastion: {
          id: `bastion_${Date.now()}`, // [FIX]: Unique ID for event tracking
          unlocked: true,
          established: false,
          name: `${prev.name ? prev.name + "'s" : 'My'} Bastion`,
          location: 'To be determined',
          createdDay: currentGameDay,
          lastBastionTurn: currentGameDay,
          nextBastionTurn: currentGameDay + 7,
          facilities: [
            {
              id: 'cramped_1',
              type: 'cramped',
              status: 'active',
              customName: null,
              builtDay: currentGameDay,
              position: [0, 0]
            },
            {
              id: 'roomy_1',
              type: 'roomy',
              status: 'active',
              customName: null,
              builtDay: currentGameDay,
              position: [2, 0]
            }
          ],
          activeOrders: [],
          // [D3 / #103]: `defenders` removed from v1 init — it is reserved for the
          //   v1.1 Bastion Defenders system (D2 system 4). The v1.1 migration
          //   transformer at docs/superpowers/specs/2026-04-28-d2-deferred-system-
          //   migration-plans.md §System 4 lazy-initializes `defenders: []` when the
          //   feature lands. Writing it here in v1 trips the runtime gate in
          //   src/services/reservedFields.js — assertNoReservedWrites().
          treasury: 0,
          history: [{
            turnNumber: 0,
            gameDay: currentGameDay,
            event: 'Bastion Founded',
            ordersCompleted: 0,
            buildingsCompleted: 2,
            goldEarned: 0,
            goldSpent: 0,
            notes: isEarlyUnlock
              ? '⚠️ Early bastion (D&D rules suggest level 5+). Your bastion has been established!'
              : 'Your bastion has been established!'
          }]
        }
      }));

      // Celebrate with confetti!
      setLevelUpLevel(playerStats.level);
      setShowConfetti(true);

      logActivity(`Bastion system unlocked at Level ${playerStats.level}${isEarlyUnlock ? ' (early unlock)' : ''}`, 'bastion', {
        level: playerStats.level,
        gameDay: currentGameDay,
        earlyUnlock: isEarlyUnlock
      });

    }
  }, [playerStats.level, playerStats.bastion, currentGameDay, logActivity, settings.bastionUnlockLevel]);

  // [R1.D2.7]: Bastion turn auto-processing now lives in a DAY_ADVANCED
  // subscriber instead of a render-driven useEffect. Reads days directly
  // from the event payload instead of tracking previousGameDayRef.
  // Architecturally mirrors the dawn-recharge subscriber added in D2.6.
  // [BATCH 13b / P1 D-24 — APR 2026]: The original blanket early-return
  // for advances longer than two days has been replaced with a multi-day
  // rollup loop in the handler below.
  //
  // The handler reads several pieces of state — we use a ref + sync effect
  // so the subscriber stays subscribed across renders without re-creating
  // the listener.
  const bastionTurnDepsRef = useRef({
    bastion: playerStats.bastion,
    level: playerStats.level,
    currentGp: currencies.gp,
    bastionUnlockLevel: settings.bastionUnlockLevel,
  });

  useEffect(() => {
    bastionTurnDepsRef.current = {
      bastion: playerStats.bastion,
      level: playerStats.level,
      currentGp: currencies.gp,
      bastionUnlockLevel: settings.bastionUnlockLevel,
    };
  }, [playerStats.bastion, playerStats.level, currencies.gp, settings.bastionUnlockLevel]);

  useEffect(() => {
    const unsub = systemBus.on(EVENTS.DAY_ADVANCED, ({ days, to: newGameDay }) => {
      const { bastion, level, currentGp, bastionUnlockLevel } = bastionTurnDepsRef.current;
      if (!bastion || !bastion.unlocked) return;
      // [FIX MAY 2026]: `established` gate — don't process turns until the
      // user has explicitly begun bastion operations via BastionPanel. The
      // auto-unlock effect creates the stub at level threshold but waits
      // for opt-in before firing weekly turns. Backward-compat: old saves
      // without `established` are treated as established (`!== false` keeps
      // them firing). Only NEW auto-unlocked bastions get `established: false`.
      if (bastion.established === false) return;
      // [v1.1 FIX]: Don't process turns if character doesn't meet current unlock level
      if (level < (bastionUnlockLevel || 5)) return;

      // [BATCH 13b / P1 D-24 — APR 2026]: Multi-day rollup. Pre-fix a
      //   blanket early-return on advances longer than two days
      //   aborted bastion processing on any week-or-longer advance —
      //   players doing downtime (week of crafting, month of training)
      //   silently lost turn results, gold, completed orders. The fix
      //   processes one bastion turn per advance-day. Each turn
      //   updates `bastion`, accumulates gold, and logs an activity
      //   entry. We cap turns at 30 to guard against runaway
      //   calendar-skip bugs (a month of bastion turns is plenty for
      //   any single advance).
      const turnsToProcess = Math.max(1, Math.min(30, days || 1));
      let workingBastion = bastion;
      let totalGoldEarned = 0;
      const turnSummaries = [];
      // newGameDay is the END day after the advance; back-calculate
      //   the per-turn day so each turn's history entry has a sensible
      //   gameDay marker.
      const startDay = (newGameDay - turnsToProcess) + 1;

      for (let i = 0; i < turnsToProcess; i++) {
        const turnDay = startDay + i;
        // [BSR-007 FIX — MAY 2026]: Thread settings so upgrade completion
        //   honors enableBastionUpgrades toggle.
        const result = processBastionTurn(workingBastion, turnDay, settings);
        if (!result) break;
        workingBastion = result.updatedBastion;
        totalGoldEarned += result.results.goldEarned || 0;
        // [FIX MAY 2026]: Emit per completed order so a top-level subscriber
        //   in App.jsx can create the inventory item. Pre-fix this lived in
        //   the App.jsx auto-process duplicate; that duplicate was deleted
        //   to stop double-firing. The event-based handoff keeps inventory
        //   creation working without re-introducing the duplicate.
        (result.results.ordersCompleted || []).forEach((order) => {
          systemBus.emit(EVENTS.BASTION_ORDER_COMPLETED, { order, gameDay: turnDay });
        });
        turnSummaries.push({
          turnNumber: workingBastion.history[0].turnNumber,
          gameDay: turnDay,
          event: result.results.event?.name,
          ordersCompleted: result.results.ordersCompleted.length,
          facilitiesCompleted: result.results.facilitiesCompleted.length,
          goldEarned: result.results.goldEarned,
        });
      }

      if (turnSummaries.length === 0) return;

      // Single setPlayerStats call with the final bastion state.
      setPlayerStats((prev) => ({ ...prev, bastion: workingBastion }));

      if (totalGoldEarned > 0) {
        const oldGold = currentGp || 0;
        const newGold = oldGold + totalGoldEarned;
        setCurrencies((prev) => ({ ...prev, gp: newGold }));
        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
          denomination: 'gp',
          oldAmount: oldGold,
          newAmount: newGold,
          change: totalGoldEarned,
          source: 'bastion-turn',
        });
      }

      // Single rollup activity entry covering all turns processed.
      const firstTurn = turnSummaries[0].turnNumber;
      const lastTurn = turnSummaries[turnSummaries.length - 1].turnNumber;
      const rangeLabel = firstTurn === lastTurn
        ? `Bastion Turn ${firstTurn}`
        : `Bastion Turns ${firstTurn}–${lastTurn}`;
      logActivity(`${rangeLabel} processed`, 'bastion', {
        gameDay: newGameDay,
        turnsProcessed: turnSummaries.length,
        ordersCompleted: turnSummaries.reduce((s, t) => s + t.ordersCompleted, 0),
        facilitiesCompleted: turnSummaries.reduce((s, t) => s + t.facilitiesCompleted, 0),
        goldEarned: totalGoldEarned,
        events: turnSummaries.map(t => t.event).filter(Boolean),
      });
    });
    return unsub;
  }, [logActivity]);  // logActivity is from useCallback; stable reference. Other deps via ref.

  // ============================================================================
  // PERSISTENCE (Save custom components and collapsed spell levels to localStorage)
  // ============================================================================

  // [BATCH 13b / P1 D-33 — APR 2026]: Subscribe to AuthContext-emitted
  //   account events and write them to the active character's activity
  //   log. AuthContext sits above CharacterContext so it can't import
  //   logActivity directly; the systemBus is the decoupled channel.
  //   Rule 13 — sign-in / sign-out / premium purchase / account
  //   deletion are meaningful actions that the retro-debugger flow
  //   should be able to surface ("when did this character last get
  //   their account upgraded?").
  useEffect(() => {
    const subs = [
      systemBus.on(EVENTS.SIGNED_IN, ({ provider }) => {
        logActivity(`Signed in (${provider || 'unknown'})`, 'account', { provider });
      }),
      systemBus.on(EVENTS.SIGNED_OUT, () => {
        logActivity('Signed out', 'account');
      }),
      systemBus.on(EVENTS.ACCOUNT_LINKED, ({ provider }) => {
        logActivity(`Account linked (${provider || 'unknown'})`, 'account', { provider });
      }),
      systemBus.on(EVENTS.ACCOUNT_UNLINKED, ({ provider }) => {
        logActivity(`Account unlinked (${provider || 'unknown'})`, 'account', { provider });
      }),
      systemBus.on(EVENTS.ACCOUNT_DELETED, () => {
        logActivity('Account deletion initiated', 'account');
      }),
      systemBus.on(EVENTS.PREMIUM_PURCHASED, () => {
        logActivity('Premium license purchased', 'account');
      }),
      systemBus.on(EVENTS.TOKENS_PURCHASED, ({ tokensAdded, totalTokens }) => {
        logActivity(
          `AI tokens purchased: +${tokensAdded ?? '?'}${totalTokens != null ? ` (total ${totalTokens})` : ''}`,
          'account',
          { tokensAdded, totalTokens }
        );
      }),
    ];
    return () => subs.forEach(unsub => unsub && unsub());
  }, [logActivity]);

  useEffect(() => {
    safeLocalStorageSet('customComponents', JSON.stringify(customComponents));
  }, [customComponents]);

  useEffect(() => {
    safeLocalStorageSet('collapsedSpellLevels', JSON.stringify(collapsedSpellLevels));
  }, [collapsedSpellLevels]);

  // [FIX NOV 18, 2025]: Auto-sync subclass spells (Circle of Land, domain spells, etc.)
  // [WHY]: Subclass spells marked alwaysPrepared: true should automatically appear in spell list
  // [INTEGRATION]: effectCalculator.getSubclassSpells() returns all subclass spells
  useEffect(() => {
    // Only run if character has classes
    if (!playerStats.classes || playerStats.classes.length === 0) return;

    // Get all subclass spells with alwaysPrepared: true
    const subclassSpells = effectCalculator.getSubclassSpells(playerStats);
    const alwaysPreparedSpells = subclassSpells.filter(s => s.alwaysPrepared === true);

    if (alwaysPreparedSpells.length === 0) return;

    // Find full spell data from SRD for each subclass spell
    const spellsToAdd = [];
    // [FIX P4-4]: Use spell NAMES, not IDs — SpellbookTab checks preparedSpells.includes(spell.name)
    const namesToAutoPrepare = [];

    alwaysPreparedSpells.forEach(subclassSpell => {
      const spellName = subclassSpell.spell;

      // Search for spell in SRD_SPELLS
      let fullSpellData = null;
      for (let level = 0; level <= 9; level++) {
        const spellAtLevel = SRD_SPELLS[level]?.find(s => s.name === spellName);
        if (spellAtLevel) {
          fullSpellData = spellAtLevel;
          break;
        }
      }

      if (fullSpellData) {
        // Check if spell already in knownSpells by name
        const existingSpell = knownSpells.find(s => s.name === spellName);

        if (existingSpell) {
          // Spell already exists, just ensure it's prepared (by NAME for SpellbookTab compatibility)
          if (!preparedSpells.includes(existingSpell.name)) {
            namesToAutoPrepare.push(existingSpell.name);
          }
        } else {
          // Spell doesn't exist, add it to knownSpells
          const newSpellId = Date.now() + Math.random();
          const classSource = subclassSpell.source.split(' - ')[0] || playerStats.classes?.[0]?.className || '';

          spellsToAdd.push({
            ...fullSpellData,
            id: newSpellId,
            classSource: classSource,
            alwaysPrepared: true // Mark it so UI can show it's always prepared
          });

          // Push the spell NAME (not ID) for preparedSpells consistency
          namesToAutoPrepare.push(fullSpellData.name);
        }
      }
    });

    // Add new spells to knownSpells if any
    if (spellsToAdd.length > 0) {
      setKnownSpells(prev => {
        // Avoid duplicates by name
        const existingNames = new Set(prev.map(s => s.name));
        const uniqueSpells = spellsToAdd.filter(s => !existingNames.has(s.name));
        if (uniqueSpells.length > 0) {
          return [...prev, ...uniqueSpells];
        }
        return prev;
      });
    }

    // Auto-prepare spells by name if any
    if (namesToAutoPrepare.length > 0) {
      setPreparedSpells(prev => {
        const newNames = namesToAutoPrepare.filter(name => !prev.includes(name));
        if (newNames.length > 0) {
          return [...prev, ...newNames];
        }
        return prev;
      });
    }
  }, [playerStats.classes, knownSpells, preparedSpells, setKnownSpells, setPreparedSpells]);

  // ============================================================================
  // CONCENTRATION EVENT SUBSCRIPTIONS (PHASE 4D SESSION 2)
  // ============================================================================

  useEffect(() => {
    // [PHASE 0.2.5 - NOV 11, 2025]: Subscribe to concentration events for UI state sync
    // [WHY]: concentrationSystem emits CONCENTRATION_STARTED events, we need to sync UI state
    // [NOTE]: We don't call startConcentration() here to avoid double-calling concentrationSystem

    const handleConcentrationStarted = (event) => {
      const { spell, spellLevel, character } = event;

      // Only update UI state, don't call concentrationSystem (already called by source)
      if (spell) {
        const spellName = typeof spell === 'string' ? spell : spell.name || spell;
        const level = spellLevel || (typeof spell === 'object' ? spell.level : 0);

        const concentrationData = {
          spellName,
          spellLevel: level,
          startTime: new Date(),
          source: 'spell'
        };

        setCurrentConcentration(concentrationData);
      }
    };

    // Subscribe to concentration started events
    systemBus.on(EVENTS.CONCENTRATION_STARTED, handleConcentrationStarted);

    // Cleanup on unmount
    return () => {
      systemBus.off(EVENTS.CONCENTRATION_STARTED, handleConcentrationStarted);
    };
  }, []); // No dependencies - just syncs UI state

  useEffect(() => {
    // [PHASE 0.2.5 - NOV 11, 2025]: Subscribe to concentration broken events for UI state sync
    // [WHY]: concentrationSystem emits CONCENTRATION_BROKEN events, we need to sync UI state
    // [NOTE]: We don't call breakConcentration() here to avoid double-calling concentrationSystem

    const handleConcentrationBroken = (event) => {
      // Only update UI state, don't call concentrationSystem (already called by source)
      setCurrentConcentration(null);
    };

    // Subscribe to concentration broken events
    systemBus.on(EVENTS.CONCENTRATION_BROKEN, handleConcentrationBroken);

    // Cleanup on unmount
    return () => {
      systemBus.off(EVENTS.CONCENTRATION_BROKEN, handleConcentrationBroken);
    };
  }, []); // No dependencies - just syncs UI state

  // [REMOVED - PHASE 0.2.5 NOV 11, 2025]: HP Changed - Trigger Concentration Saves
  // [WHY REMOVED]: VitalStatsCard.updateHP() now calls concentrationSystem.checkConcentration() directly
  // [BENEFIT]: Automatic damage checks with proper save rolls, no manual resolution needed
  // [OLD CODE]: Used to listen to HP_CHANGED events and set pendingConcentrationSave state

  // ============================================================================
  // EFFECT REGISTRY INITIALIZATION
  // ============================================================================

  // [PHASE 1.2 FIX - NOV 15, 2025]: Re-register ALL effects on load/change
  // [PURPOSE]: EffectRegistry is in-memory only and doesn't persist to localStorage
  // [WHY]: When page reloads, effectRegistry is empty even though effects exist in playerStats
  // [FIX]: Re-register ALL effect sources (feats, items, conditions, spells) on mount and change
  // [IMPACT]: Mobile feat (+10 speed), Belt of Giant Strength (STR=21), Bless, etc. now work after page reload
  useEffect(() => {

    // [A2-stunningstrike-spendmenu-coldload FIX — 2026-05-30]: was effectRegistry.clear()
    //   — a registry-WIDE wipe. This effect re-registers ONLY the feat/condition/spell
    //   families (below); class/race/subclass/boon/expertise effects are owned by separate
    //   always-mounted App.jsx hooks (useClassFeatureEffects, useRaceFeatureEffects,
    //   useSubclassFeatureEffects, useBoonEffects, useExpertiseEffects). CharacterContext is
    //   the PARENT provider, so this effect runs AFTER those child hooks on a shared commit —
    //   the blanket clear() wiped their just-registered effects, and on a commit where only
    //   feats/conditions/activeSpells changed (the deps below) the child hooks did NOT re-run,
    //   leaving e.g. the Monk Ki Spend Menu (spendActionMenu) gone → the "Spend Options pane
    //   absent on cold load" bug (runtime-instrumentation-confirmed 2026-05-30). Scope the
    //   cleanup to THIS effect's own prefixes: net-identical for feat:/condition:/spell:
    //   (clear() already removed all of those), but class:/race:/subclass:/boon:/expertise:
    //   now survive. Each owning hook self-cleans its own prefix via removeByPrefix before
    //   re-registering, so cross-character staleness is still handled per-owner.
    effectRegistry.removeByPrefix('feat:');
    effectRegistry.removeByPrefix('condition:');
    effectRegistry.removeByPrefix('spell:');

    // 1. Re-register feat effects
    // [P1.1 — APR 2026]: Pass playerStats.customFeats so custom feats
    // (id: `custom_<timestamp>`) get their effects registered on mount,
    // not just SRD/source feats. Pre-fix custom feats silently produced
    // null featData and never made it into the registry.
    const feats = playerStats.feats || [];
    feats.forEach(feat => {
      const featData = getFeatData(feat.id, playerStats.customFeats);
      if (featData?.effects && Array.isArray(featData.effects)) {
        try {
          effectRegistry.register(`feat:${feat.id}`, featData.effects, playerStats);
        } catch (error) {
          console.warn(`[CharacterContext] Failed to re-register feat ${feat.id}:`, error);
        }
      }
    });

    // 2. [FIX phase6-E E-3]: Item effects are re-registered by InventoryContext
    //    via its own useEffect on the canonical inventory array. The previous
    //    `playerStats.equipment?.equipped` path was broken (the property
    //    doesn't exist — items live on InventoryContext.inventory with
    //    `equipped: true` + `equipSlot`). InventoryContext is downstream of
    //    CharacterContext, so by the time it mounts and runs its registration
    //    pass, this clear() has already fired. Don't re-register items here.

    // 3. Re-register active condition effects
    // [Phase B / #233 FIX]: playerStats.conditions is an array of STRING keys
    //   (e.g. ['grappled', 'poisoned']), NOT objects with .id/.effects properties.
    //   The previous loop accessed condition.effects and condition.id — both
    //   undefined on strings — so the guard always skipped every entry, leaving
    //   effectRegistry empty of condition effects after any state change or reload.
    //   UI still showed conditions as active because effectCalculator.hasCondition()
    //   reads the string array directly, but stat modifiers (speed=0 for Grappled,
    //   auto-fail saves for Paralyzed/Stunned, etc.) were silently gone.
    //   Fix: look up each string key in SRD_CONDITIONS and register numericEffects.
    const conditions = playerStats.conditions || [];
    conditions.forEach(conditionKey => {
      const conditionData = SRD_CONDITIONS[conditionKey];
      if (conditionData?.numericEffects && Array.isArray(conditionData.numericEffects)) {
        try {
          effectRegistry.register(`condition:${conditionKey}`, conditionData.numericEffects, playerStats);
        } catch (error) {
          console.warn(`[CharacterContext] Failed to re-register condition ${conditionKey}:`, error);
        }
      }
    });

    // 4. Re-register active spell effects (Bless, Shield of Faith, etc.)
    const activeSpells = playerStats.activeSpells || [];
    activeSpells.forEach(spell => {
      if (spell.effects && Array.isArray(spell.effects)) {
        try {
          effectRegistry.register(`spell:${spell.id}`, spell.effects, playerStats);
        } catch (error) {
          console.warn(`[CharacterContext] Failed to re-register spell ${spell.id}:`, error);
        }
      }
    });

  }, [playerStats.feats, playerStats.conditions, playerStats.activeSpells]); // Re-register when any effect source changes
  // NOTE: playerStats.equipment removed — items live on InventoryContext.inventory (canonical path),
  //       not playerStats.equipment (ghost property). Item effects are re-registered by
  //       InventoryContext's own useEffect on the canonical inventory array. See E-3 fix (f5613c4).

  // [Fix 1.10 — APR 2026 release triage]: Re-register custom feat resource pools
  // on character mount / customFeats change.
  // [WHY]: The global custom resource pool registry (classResources.customResourcePools)
  //   is in-memory only. On page reload it starts empty even though customFeats
  //   in playerStats already define pools. Without this re-registration, the
  //   autocomplete on the FeatsPanel name input + ClassResourcesPanel would
  //   show no entries until the player edits an existing feat.
  // [IDEMPOTENT]: registerCustomResourcePool overwrites by id, so this hook is
  //   safe to fire on every customFeats change.
  useEffect(() => {
    const customFeats = playerStats.customFeats || [];
    customFeats.forEach(feat => {
      const pool = feat?.resourcePool;
      if (!pool || !pool.id || !pool.name) return;
      try {
        registerCustomResourcePool({
          id: pool.id,
          name: pool.name,
          max: pool.quantity,
          recharge: pool.rechargeOn,
          dieSize: pool.dieSize,
          quantity: pool.quantity,
        });
      } catch (error) {
        console.warn(`[CharacterContext] Failed to re-register custom resource pool ${pool.id}:`, error);
      }
    });
  }, [playerStats.customFeats]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // [PHASE 1.3.2 - NOV 11, 2025]: Backwards Compatibility for Property Names
  // [PURPOSE]: Provide computed property aliases for backwards compatibility
  // [WHY]: Code uses multiple property names for same values (exhaustion, HP variants)
  // [PATTERN]: Expose canonical names + common aliases to prevent undefined access
  const playerStatsWithComputed = React.useMemo(() => ({
    ...playerStats,

    // [CANONICAL]: exhaustionLevel (stored in timeSystem.exhaustionLevel)
    // [ALIASES]: exhaustion (backwards compatibility)
    exhaustion: timeSystem?.exhaustionLevel ?? 0,
    exhaustionLevel: timeSystem?.exhaustionLevel ?? 0,

    // [CANONICAL]: maxHitPoints, currentHitPoints, temporaryHitPoints
    // [ALIASES]: maxHP, maxHp, hp, tempHp (backwards compatibility)
    // [WHY]: Some components use maxHP/maxHp, others maxHitPoints - provide all variants
    // [PHASE 2.4 - NOV 15, 2025]: Added maxHP (capital P) variant for complete backward compatibility
    maxHP: playerStats.maxHitPoints,    // Alias for backward compatibility
    maxHp: playerStats.maxHitPoints,    // Alias for backward compatibility
    hp: playerStats.currentHitPoints,
    tempHp: playerStats.temporaryHitPoints
  }), [playerStats, timeSystem]);

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // Core Character State (with computed properties for backwards compatibility)
    playerStats: playerStatsWithComputed,
    setPlayerStats,
    currencies,
    setCurrencies,
    skillProficiencies,
    setSkillProficiencies,
    temporaryStats,
    setTemporaryStats,

    // Time and Calendar
    currentGameDay,
    setCurrentGameDay,
    advanceDay,
    timeSystem,
    setTimeSystem,
    calendarSettings,
    setCalendarSettings,

    // Spell System
    knownSpells,
    setKnownSpells,
    preparedSpells,
    setPreparedSpells,
    customSpells,
    setCustomSpells,
    spellSlots,
    setSpellSlots,
    manualSpellSlots,
    setManualSpellSlots,
    activeSpellbookClass,
    setActiveSpellbookClass,
    collapsedSpellLevels,
    setCollapsedSpellLevels,
    customComponents,
    setCustomComponents,

    // Concentration Tracking (Phase 4D)
    currentConcentration,
    setCurrentConcentration,

    // Concentration Saves (Phase 0.2B - Nov 11, 2025)
    pendingConcentrationSave,
    requireConcentrationSave,
    resolveConcentrationSave,

    // Activity and Journal
    activityLog,
    setActivityLog,
    journalEntries,
    setJournalEntries,
    rollTables, // [PHASE 4C]
    setRollTables, // [PHASE 4C]
    craftingProjects,
    setCraftingProjects,
    dmEvents,
    setDmEvents,

    // Quick Bonuses
    activeQuickBonuses,
    setActiveQuickBonuses,
    customQuickBonuses,
    setCustomQuickBonuses,
    inspirationPrompt,
    setInspirationPrompt,

    // UI State
    showCharacterWizard,
    setShowCharacterWizard,
    showLevelUpWizard,
    setShowLevelUpWizard,
    showKeyboardHelp,
    setShowKeyboardHelp,
    showConfetti,
    setShowConfetti,
    levelUpLevel,
    setLevelUpLevel,

    // Actions
    logActivity,
    updateHP,
    updateMaxHP,
    updateTemporaryHP,
    updateDeathSaves,
    resetDeathSaves,
    updateInspiration,
    updateLevel,
    updateXP,
    updateCurrency,
    addCurrency,
    subtractCurrency,
    useSpellSlot,
    restoreSpellSlot,
    restoreAllSpellSlots,
    addKnownSpell,
    removeKnownSpell,
    prepareSpell,
    unprepareSpell,
    startConcentration,
    breakConcentration,
    isConcentratingOn,
    getConcentration,
    addQuickBonus,
    removeQuickBonus,
    clearAllQuickBonuses
  }), [
    // State values
    playerStatsWithComputed, currencies, skillProficiencies, temporaryStats,
    currentGameDay, timeSystem, calendarSettings,
    knownSpells, preparedSpells, customSpells, spellSlots, manualSpellSlots,
    activeSpellbookClass, collapsedSpellLevels, customComponents,
    currentConcentration, pendingConcentrationSave,
    activityLog, journalEntries, rollTables, craftingProjects, dmEvents,
    activeQuickBonuses, customQuickBonuses, inspirationPrompt,
    showCharacterWizard, showLevelUpWizard, showKeyboardHelp, showConfetti, levelUpLevel,
    // Stable references (setters and useCallback functions — included for ESLint exhaustive-deps)
    setPlayerStats, setCurrencies, setSkillProficiencies, setTemporaryStats,
    setCurrentGameDay, setTimeSystem, setCalendarSettings, advanceDay,
    setKnownSpells, setPreparedSpells, setCustomSpells, setSpellSlots, setManualSpellSlots,
    setActiveSpellbookClass, setCollapsedSpellLevels, setCustomComponents,
    setCurrentConcentration,
    setActivityLog, setJournalEntries, setRollTables, setCraftingProjects, setDmEvents,
    setActiveQuickBonuses, setCustomQuickBonuses, setInspirationPrompt,
    setShowCharacterWizard, setShowLevelUpWizard, setShowKeyboardHelp, setShowConfetti, setLevelUpLevel,
    logActivity, updateHP, updateMaxHP, updateTemporaryHP, updateDeathSaves, resetDeathSaves,
    updateInspiration, updateLevel, updateXP, updateCurrency, addCurrency, subtractCurrency,
    useSpellSlot, restoreSpellSlot, restoreAllSpellSlots,
    addKnownSpell, removeKnownSpell, prepareSpell, unprepareSpell,
    startConcentration, breakConcentration, isConcentratingOn, getConcentration,
    requireConcentrationSave, resolveConcentrationSave,
    addQuickBonus, removeQuickBonus, clearAllQuickBonuses
  ]);

  return (
    <CharacterContext.Provider value={value}>
      {children}
    </CharacterContext.Provider>
  );
};

/**
 * Hook to access CharacterContext
 * @throws {Error} if used outside CharacterProvider
 *
 * [FIX - NOV 3, 2025]: Enhanced error message with debugging hints
 */
export const useCharacter = () => {
  const context = useContext(CharacterContext);
  if (!context) {
    console.error('❌ useCharacter() called outside CharacterProvider!');
    console.error('📍 Check that your component is wrapped in <AppProviders>');
    console.error('📍 File: src/contexts/CharacterContext.js');
    throw new Error(
      'useCharacter must be used within CharacterProvider. ' +
      'Ensure your component is wrapped in <AppProviders>. ' +
      'See src/contexts/CharacterContext.js:817'
    );
  }
  return context;
};

export default CharacterContext;
